import { MiddlewareNextFunction, ReduxStore } from '../../types/reduxTypes';
import { Action, UnknownAction } from 'redux';
import { ELEMENT_CREATE, ELEMENT_MOVE_MULTI, ELEMENT_UPDATE } from '../../../common/elements/elementConstants';
import {
    ElementCreateAction,
    ElementMoveMultiAction,
    ElementUpdateAction,
    ElementUpdateEntry,
} from '../../../common/elements/elementActionTypes';
import { getElements } from '../selectors/elementsSelector';
import { getElement } from '../../../common/elements/utils/elementTraversalUtils';
import { getElementAttachedInlineCommentIds } from '../../../common/comments/attachedCommentUtil';
import { defer, difference } from 'lodash';
import { element as elementReducer } from '../reducers/elementReducer';
import { AttachMode, EditorContent, ImMNElement } from '../../../common/elements/elementModelTypes';
import { moveElementsToTrash } from '../actions/elementShortcutActions';
import { getCurrentBoardId } from '../selectors/currentBoardSelector';
import { getElementLocation, getTextContent } from '../../../common/elements/utils/elementPropertyUtils';
import { removeEditorJsonInlineComment } from '../../../common/tiptap/utils/jsonContentUtils/manipulation/updateEditorJsonInlineComment';
import { asObject, isEmpty } from '../../../common/utils/immutableHelper';
import { updateMultipleElements } from '../actions/elementActions';
import { BoardSections } from '../../../common/boards/boardConstants';
import { POPUP_CLOSE } from '../../components/popupPanel/popupConstants';
import { PopupCloseAction } from '../../components/popupPanel/popupActionTypes';
import { COLLAPSED_COMMENT_POPUP_PREFIX } from '../../../common/elements/utils/collapsedCommentPopupUtils';
import { getSelectedElementIds } from '../selection/selectedElementsSelector';
import { isLocationAttachedInline } from '../../../common/elements/utils/elementLocationUtils';
import { removeSelectedElements } from '../selection/selectionActions';
import { isCard } from '../../../common/elements/utils/elementTypeUtils';
import getEditorJsonInlineCommentIds from '../../../common/tiptap/utils/jsonContentUtils/getEditorJsonInlineCommentIds';
import { moveMultipleElements } from '../actions/elementMoveActions';
import { ActionOrigin } from '../../../common/actions/actionTypes';

/**
 * Determines if a move is from an attached inline comment to elsewhere.
 */
const isInlineAttachedMovement = (move: ElementMoveMultiAction['moves'][0]): boolean => {
    if (!move) return false;
    if (move.from?.section !== BoardSections.ATTACHED) return false;
    if (move.from?.position?.attachMode !== AttachMode.inline) return false;

    // Changing parent so we definitely need to handle this
    if (move.from?.parentId !== move.location?.parentId) return true;

    return move.location?.position?.attachMode !== AttachMode.inline;
};

/**
 * If an attached inline comment is moved - we need to remove the inline comment from the previous
 * parent element's textContent.
 */
const handleElementMoveMulti = (action: ElementMoveMultiAction, store: ReduxStore): ElementMoveMultiAction => {
    const state = store.getState();

    const { moves } = action;
    if (moves.length === 0) return action;

    const allStoreElements = getElements(state);
    const removedInlineCommentThreadMoves = moves.filter(isInlineAttachedMovement);

    if (removedInlineCommentThreadMoves.length === 0) return action;

    const updates: ElementUpdateEntry[] = [];

    for (const inlineCommentThreadMove of removedInlineCommentThreadMoves) {
        const inlineCommentElement = getElement(allStoreElements, inlineCommentThreadMove.from?.parentId);

        if (!inlineCommentElement) continue;

        const movedCommentThreadId = inlineCommentThreadMove.id;

        const initialTextContent = asObject(getTextContent(inlineCommentElement)) as EditorContent;
        const updatedTextContent = removeEditorJsonInlineComment(initialTextContent, movedCommentThreadId);

        updates.push({
            id: inlineCommentThreadMove.from!.parentId!,
            changes: { textContent: updatedTextContent },
        });
    }

    if (updates.length > 0) {
        store.dispatch(
            updateMultipleElements({
                updates,
                transactionId: action.transactionId,
                actionOrigin: ActionOrigin.attachedCommentMiddleware,
            }),
        );
    }

    return action;
};

/**
 * If an element update occurs that removes inline comments (i.e. textContent changes that remove inline comment marks),
 * we need to also move the corresponding attached comments to trash.
 */
const handleElementTextContentChange = (action: ElementUpdateAction, store: ReduxStore): ElementUpdateAction | null => {
    // When text is clipped - we don't want to handle any attached comment updates here.
    // Instead the handleElementCreate function will handle moving any inline comments to the new card.
    if (action.actionOrigin === ActionOrigin.textClipper) return null;

    const state = store.getState();

    const { updates } = action;

    // We're only interested in updates that potentially affect inline comments, i.e. textContent changes
    const textContentChanges = updates.filter((update) => 'textContent' in update.changes);
    if (textContentChanges.length === 0) return null;

    const currentBoardId = getCurrentBoardId(state);

    const allStoreElements = getElements(state);
    const ids = textContentChanges.map((elem) => elem.id);
    const originalElements = ids.map((id) => getElement(allStoreElements, id));

    for (const originalElement of originalElements) {
        if (!originalElement) continue;

        const attachedInlineCommentIds = getElementAttachedInlineCommentIds(originalElement);

        if (isEmpty(attachedInlineCommentIds)) continue;

        const elementAfterUpdate = elementReducer(originalElement, action) as ImMNElement;

        const updatedAttachedInlineCommentIds = getElementAttachedInlineCommentIds(elementAfterUpdate);

        const removedInlineCommentIds = difference(attachedInlineCommentIds, updatedAttachedInlineCommentIds);

        if (isEmpty(removedInlineCommentIds)) continue;

        store.dispatch(
            moveElementsToTrash({
                currentBoardId,
                elementIds: removedInlineCommentIds,
                keepSelection: true,
                transactionId: action.transactionId,
                actionOrigin: ActionOrigin.attachedCommentMiddleware,
            }),
        );
    }

    return action;
};

/**
 * When an attached inline comment is closed, if it's the currently selected element, we need to
 * remove it from the selection, because the comment message bubble is not visible.
 */
const handlePopupCloseAction = (action: PopupCloseAction, store: ReduxStore): PopupCloseAction => {
    const { popupId } = action;

    if (!popupId || !popupId.startsWith(COLLAPSED_COMMENT_POPUP_PREFIX)) return action;

    const state = store.getState();
    const currentlySelectedElementIds = getSelectedElementIds(state);

    if (isEmpty(currentlySelectedElementIds)) return action;

    const firstSelectedElementId = currentlySelectedElementIds.first();
    const closedCommentThreadId = popupId.replace(COLLAPSED_COMMENT_POPUP_PREFIX, '');

    // Get the currently selected element ID
    if (firstSelectedElementId !== closedCommentThreadId) return action;

    const elements = getElements(state);
    const closedCommentThread = getElement(elements, closedCommentThreadId);

    if (!isLocationAttachedInline(closedCommentThread)) return action;

    // Need to defer, to prevent the selection from changing before the popup actually closes.
    //  This is important for selecting custom colours
    defer(() => {
        store.dispatch(removeSelectedElements({ ids: [closedCommentThreadId], transactionId: null }));
    });

    return action;
};

/**
 * When an element is updated, check to see if any synced changes to an inline comment or attached comment are needed.
 */
const handleElementUpdate = (action: ElementUpdateAction, store: ReduxStore): ElementUpdateAction =>
    handleElementTextContentChange(action, store) || action;

/**
 * If a card is created with an attached inline comment, we need to move that comment thread to be attached
 * to the new card.
 */
const handleElementCreate = (action: ElementCreateAction, store: ReduxStore): ElementCreateAction => {
    if (!isCard(action)) return action;
    if (!action.content?.textContent) return action;

    const inlineCommentIds = getEditorJsonInlineCommentIds(action.content.textContent as EditorContent);

    if (isEmpty(inlineCommentIds)) return action;

    const state = store.getState();
    const allStoreElements = getElements(state);

    // This element will be created when the action is processed, (after this function completes),
    //  but we know its ID ahead of time
    const destinationElementId = action.id;

    const moves = inlineCommentIds.map((commentThreadId) => {
        const commentThreadElement = getElement(allStoreElements, commentThreadId);

        const from = asObject(getElementLocation(commentThreadElement));

        return {
            id: commentThreadId,
            from,
            location: {
                parentId: destinationElementId,
                section: BoardSections.ATTACHED,
                position: { attachMode: AttachMode.inline },
            },
        };
    });

    // This needs to be deferred to allow the element it's moving to to be created first
    defer(() => {
        store.dispatch(
            moveMultipleElements({
                moves,
                transactionId: action.transactionId,
                actionOrigin: ActionOrigin.attachedCommentMiddleware,
                moveOperation: null,
                initialMeasurements: null,
            }),
        );
    });

    return action;
};

const handleAction = (action: UnknownAction, store: ReduxStore): Action => {
    switch (action.type) {
        case ELEMENT_MOVE_MULTI:
            return handleElementMoveMulti(action as unknown as ElementMoveMultiAction, store);
        case ELEMENT_UPDATE:
            return handleElementUpdate(action as unknown as ElementUpdateAction, store);
        case POPUP_CLOSE:
            return handlePopupCloseAction(action as unknown as PopupCloseAction, store);
        case ELEMENT_CREATE:
            return handleElementCreate(action as unknown as ElementCreateAction, store);
        default:
            return action;
    }
};

/**
 * This middleware handles actions that relate to keeping attached comments
 * in sync with their inline comments.
 *
 * Because the inline comments are stored as marks in the Tiptap document,
 * but the attached comments are stored as separate elements, we need to
 * ensure that when an inline comment is added or removed, the corresponding
 * attached comment element is also created or deleted.
 */
export default (store: ReduxStore) => (next: MiddlewareNextFunction) => async (action: UnknownAction) => {
    const shouldSkipHandling =
        action.remote ||
        action.isUndo ||
        action.isRedo ||
        action.actionOrigin === ActionOrigin.attachedCommentMiddleware;

    if (shouldSkipHandling) return next(action);

    const nextAction = handleAction(action, store);

    return next(nextAction);
};
